import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addStore, submitStoreRating } from "../../webService/webService";
import { queryKeys } from "../queryKeys";

export const useAddStore = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ storeData, token }) => addStore(storeData, token),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.stores.all });
      options.onSuccess?.(...args);
    },
  });
};

/**
 * Submits a store rating. The clicked star count is applied to the user's
 * own-rating cache optimistically (so the stars update the instant you
 * click, not after a round trip), rolled back on failure, and on
 * success/settle both that cache and the store's aggregate rating stats are
 * invalidated — so the displayed average/count update too, instead of
 * staying stale until a full page reload (the previous behavior in both
 * StoreInfo.jsx and LiquorStoreInfo.jsx — see webService.jsx's
 * submitStoreRating, which never triggered a refetch of fetchStoreRatingStats).
 */
export const useSubmitStoreRating = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: ({ storeId, rating, uid, token }) => submitStoreRating(storeId, rating, uid, token),
    onMutate: async ({ storeId, rating, uid }) => {
      const userRatingKey = queryKeys.ratings.user(storeId, uid);
      await queryClient.cancelQueries({ queryKey: userRatingKey });
      const previousUserRating = queryClient.getQueryData(userRatingKey);
      queryClient.setQueryData(userRatingKey, (old) => ({ ...old, rating }));
      return { previousUserRating, userRatingKey };
    },
    onError: (err, variables, context) => {
      if (context) queryClient.setQueryData(context.userRatingKey, context.previousUserRating);
      options.onError?.(err, variables, context);
    },
    onSuccess: (...args) => options.onSuccess?.(...args),
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ratings.stats(variables.storeId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ratings.user(variables.storeId, variables.uid) });
      options.onSettled?.(data, error, variables, context);
    },
  });
};
